import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { AuthShell, Field } from "./Login";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 1800);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  if (!token) {
    return (
      <AuthShell title="Reset link invalid" subtitle="This link is missing or malformed">
        <div className="text-center py-6" data-testid="reset-invalid">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-700">Please request a new password reset link.</p>
          <Link to="/forgot-password" className="inline-block mt-6 text-amber-600 font-semibold">Request a new link</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a strong password for your account">
      {done ? (
        <div className="text-center py-6" data-testid="reset-success">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
          <p className="text-slate-700">Your password has been updated. Redirecting you to login…</p>
          <Link to="/login" className="inline-block mt-6 text-amber-600 font-semibold">Go to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3" data-testid="reset-error">{error}</p>}
          <Field label="New password" type="password" value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} testid="reset-password" autoComplete="new-password" />
          <Field label="Confirm new password" type="password" value={form.confirm} onChange={(v) => setForm((f) => ({ ...f, confirm: v }))} testid="reset-confirm" autoComplete="new-password" />
          <button disabled={loading} className="w-full inline-flex justify-center items-center gap-2 bg-slate-900 text-white font-semibold py-3 rounded-lg disabled:opacity-60" data-testid="reset-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
          </button>
          <p className="text-sm text-center text-slate-500"><Link to="/login" className="text-amber-600 font-semibold">Back to login</Link></p>
        </form>
      )}
    </AuthShell>
  );
}
